import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import VideoUploadField from '../../components/VideoUploadField';
import {
  getKioskLoopVideos,
  createKioskLoopVideo,
  updateKioskLoopVideo,
  deleteKioskLoopVideo,
  getArtists,
} from '../../services/adminService';

const KIOSK_LOOP_VIDEO_BUCKET = 'kiosk_loop_videos';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: '600',
  color: '#555',
  marginBottom: '0.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const emptyForm = { video_url: '', title: '', instagram_username: '', text_color: 'white' };

const selectStyle = {
  padding: '0.4rem 0.6rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.85rem',
  outline: 'none',
  background: 'white',
};

const AdminKioskLoopPage = () => {
  const { session } = useAuth();
  const token = session?.access_token;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [artistsWithVideo, setArtistsWithVideo] = useState([]);
  const [artistPicker, setArtistPicker] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [isUploading, setIsUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const [colorChangingId, setColorChangingId] = useState(null);

  const load = () => {
    if (!token) { setLoading(false); setError('No hay sesión activa.'); return; }
    setLoading(true);
    getKioskLoopVideos(token)
      .then((data) => { setVideos(data.videos || []); setError(null); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [token]);

  // Artists already have a studio video (uploaded from their profile form) —
  // offer those as a source so staff don't have to re-upload the same file.
  useEffect(() => {
    if (!token) return;
    getArtists(token, { limit: 100 })
      .then((data) => {
        const withVideo = (data.artists || [])
          .filter((a) => a.studioVideoUrl)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setArtistsWithVideo(withVideo);
      })
      .catch(() => {}); // non-critical — the picker just won't offer options
  }, [token]);

  const handlePickArtist = (artistId) => {
    setArtistPicker(artistId);
    const artist = artistsWithVideo.find((a) => String(a.id) === artistId);
    if (!artist) return;
    setForm((prev) => ({
      ...prev,
      video_url: artist.studioVideoUrl || '',
      title: artist.name || '',
      instagram_username: artist.instagramHandle || '',
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!form.video_url.trim()) {
      setFormError('Sube un video o pega una URL antes de guardar.');
      return;
    }

    setSaving(true);
    try {
      await createKioskLoopVideo(token, {
        video_url: form.video_url.trim(),
        title: form.title.trim() || null,
        instagram_username: form.instagram_username.trim() || null,
        text_color: form.text_color,
      });
      setForm(emptyForm);
      setArtistPicker('');
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (video) => {
    setTogglingId(video.id);
    try {
      const updated = await updateKioskLoopVideo(token, video.id, { active: !video.active });
      setVideos((prev) => prev.map((v) => (v.id === video.id ? { ...v, active: updated.video.active } : v)));
    } catch (err) {
      alert(`Error al actualizar: ${err.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleTextColorChange = async (video, text_color) => {
    setColorChangingId(video.id);
    try {
      const updated = await updateKioskLoopVideo(token, video.id, { text_color });
      setVideos((prev) => prev.map((v) => (v.id === video.id ? { ...v, text_color: updated.video.text_color } : v)));
    } catch (err) {
      alert(`Error al actualizar: ${err.message}`);
    } finally {
      setColorChangingId(null);
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`¿Quitar "${video.title || video.video_url}" del video loop?`)) return;
    setDeletingId(video.id);
    try {
      await deleteKioskLoopVideo(token, video.id);
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
    } catch (err) {
      alert(`Error al eliminar: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= videos.length) return;

    const a = videos[index];
    const b = videos[target];
    setReorderingId(a.id);
    try {
      await Promise.all([
        updateKioskLoopVideo(token, a.id, { display_order: b.display_order }),
        updateKioskLoopVideo(token, b.id, { display_order: a.display_order }),
      ]);
      const next = [...videos];
      next[index] = { ...b, display_order: a.display_order };
      next[target] = { ...a, display_order: b.display_order };
      setVideos(next);
    } catch (err) {
      alert(`Error al reordenar: ${err.message}`);
    } finally {
      setReorderingId(null);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Video loop del kiosco
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {videos.length} video{videos.length !== 1 ? 's' : ''}
        </span>
      </div>
      <p style={{ color: '#888', fontSize: '0.88rem', marginTop: 0, marginBottom: '1.5rem' }}>
        Videos que se reproducen en loop cuando el kiosco está inactivo. Al tocar la pantalla, el visitante
        pasa al catálogo de artistas. Solo los videos <strong>activos</strong> aparecen en el kiosco, en el
        orden mostrado abajo.
      </p>

      <form
        onSubmit={handleCreate}
        style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1.25rem', marginBottom: '1.5rem' }}
      >
        <h2 style={{ fontSize: '1rem', color: '#333', marginTop: 0, marginBottom: '1rem' }}>Agregar video</h2>

        {artistsWithVideo.length > 0 && (
          <div style={{ marginBottom: '1.1rem' }}>
            <label style={labelStyle}>Usar el video de un artista existente (opcional)</label>
            <select
              value={artistPicker}
              onChange={(e) => handlePickArtist(e.target.value)}
              style={{ ...selectStyle, width: '100%' }}
            >
              <option value="">— Selecciona un artista —</option>
              {artistsWithVideo.map((artist) => (
                <option key={artist.id} value={artist.id}>{artist.name}</option>
              ))}
            </select>
            <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
              Usa el "Video del estudio" ya subido en el perfil del artista y llena el título e Instagram
              por ti — puedes editarlos antes de guardar.
            </span>
          </div>
        )}

        <VideoUploadField
          label="Video — vertical 1080×1920 recomendado"
          value={form.video_url}
          onChange={(url) => setForm((prev) => ({ ...prev, video_url: url }))}
          bucket={KIOSK_LOOP_VIDEO_BUCKET}
          onUploadingChange={setIsUploading}
          helperText="MP4, WebM o MOV. Máx. 100 MB."
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
          <div>
            <label style={labelStyle}>Título</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Nombre del artista o del clip"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Usuario de Instagram</label>
            <input
              type="text"
              value={form.instagram_username}
              onChange={(e) => setForm((prev) => ({ ...prev, instagram_username: e.target.value }))}
              placeholder="usuario (sin @)"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Color del texto</label>
            <select
              value={form.text_color}
              onChange={(e) => setForm((prev) => ({ ...prev, text_color: e.target.value }))}
              style={{ ...selectStyle, width: '100%' }}
            >
              <option value="white">Blanco</option>
              <option value="black">Negro</option>
            </select>
          </div>
        </div>

        {formError && <p style={{ color: '#dc3545', fontSize: '0.85rem', marginTop: '0.75rem' }}>{formError}</p>}

        <button
          type="submit"
          disabled={saving || isUploading}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1.25rem',
            background: saving || isUploading ? '#f8f9fa' : '#7c3aed',
            color: saving || isUploading ? '#aaa' : 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.9rem',
            cursor: saving || isUploading ? 'default' : 'pointer',
          }}
        >
          {saving ? 'Guardando...' : isUploading ? 'Subiendo video...' : '+ Agregar al loop'}
        </button>
      </form>

      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Orden', 'Video', 'Título', 'Instagram', 'Color del texto', 'Activo', ''].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td></tr>
              ) : videos.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Aún no hay videos en el loop.</td></tr>
              ) : videos.map((video, index) => (
                <tr key={video.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <button
                        onClick={() => handleMove(index, -1)}
                        disabled={index === 0 || reorderingId === video.id}
                        style={{ background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? '#ddd' : '#666', padding: 0, lineHeight: 1 }}
                        aria-label="Subir"
                      >▲</button>
                      <button
                        onClick={() => handleMove(index, 1)}
                        disabled={index === videos.length - 1 || reorderingId === video.id}
                        style={{ background: 'none', border: 'none', cursor: index === videos.length - 1 ? 'default' : 'pointer', color: index === videos.length - 1 ? '#ddd' : '#666', padding: 0, lineHeight: 1 }}
                        aria-label="Bajar"
                      >▼</button>
                    </div>
                  </td>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    <video
                      src={video.video_url}
                      muted
                      loop
                      playsInline
                      autoPlay
                      style={{ width: 44, height: 78, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', background: '#000' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#333' }}>{video.title || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#888' }}>
                    {video.instagram_username ? `@${video.instagram_username}` : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <select
                      value={video.text_color || 'white'}
                      onChange={(e) => handleTextColorChange(video, e.target.value)}
                      disabled={colorChangingId === video.id}
                      style={selectStyle}
                    >
                      <option value="white">Blanco</option>
                      <option value="black">Negro</option>
                    </select>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <button
                      onClick={() => handleToggleActive(video)}
                      disabled={togglingId === video.id}
                      style={{
                        background: 'none', border: 'none', cursor: togglingId === video.id ? 'default' : 'pointer',
                        color: togglingId === video.id ? '#ccc' : video.active ? '#28a745' : '#aaa',
                        fontWeight: '600', fontSize: '0.82rem', padding: 0,
                      }}
                    >
                      {togglingId === video.id ? '...' : video.active ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <button
                      onClick={() => handleDelete(video)}
                      disabled={deletingId === video.id}
                      style={{
                        background: 'none', border: 'none',
                        color: deletingId === video.id ? '#ccc' : '#dc3545',
                        cursor: deletingId === video.id ? 'default' : 'pointer',
                        fontSize: '0.85rem', fontWeight: '500', padding: 0, whiteSpace: 'nowrap',
                      }}
                    >
                      {deletingId === video.id ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminKioskLoopPage;
